import { createContext, PropsWithChildren } from 'react'

import {
  useUserServiceGetUserProfileEndpoint,
  useUserServiceGetUserRolesListEndpoint
} from 'src/api/queries'
import { GetUserProfileResponse } from 'src/api/requests'
import { LoadingScreen } from 'src/components'

import { useSessionContext } from './SessionContext'

type UserContextType = {
  userData?: GetUserProfileResponse
  hasPermission: (permission: string | string[]) => boolean | undefined
}

export const UserContext = createContext<UserContextType | undefined>(undefined)

export const UserProvider = ({ children }: PropsWithChildren) => {
  const { session } = useSessionContext()

  const { data: userData, isLoading: isUserDataLoading } =
    useUserServiceGetUserProfileEndpoint([], {
      enabled: !!session
    })

  const { data: userRoles, isLoading } = useUserServiceGetUserRolesListEndpoint(
    [],
    {
      enabled: !!userData
    }
  )

  const checkPermission = (permission: string) => {
    const currentRole = userRoles?.data?.find(
      (role) => role?.id === userData?.currentRoleId
    )

    return currentRole?.permissions?.some(
      (permissionValue) => permissionValue?.permissionKey === permission
    )
  }

  const hasPermission = (permission: string | string[]): boolean => {
    if (Array.isArray(permission)) {
      return permission.some((perm) => checkPermission(perm))
    }

    return checkPermission(permission) ?? false
  }

  if (isUserDataLoading || isLoading) {
    return <LoadingScreen />
  }

  return (
    <UserContext.Provider value={{ userData, hasPermission }}>
      {children}
    </UserContext.Provider>
  )
}
